import CreateButton from "./CreateButton"
import { useEffect, useRef, useState } from 'react'
import api from '../api'
import Button from './Button'
import Dialog from './dialogs/Dialog'
import styles from './ManualApplicationButton.module.css'

const errorText = error => typeof error?.detail === 'string' ? error.detail : 'Operazione non riuscita. Controlla i dati e riprova.'
const STEP_LABELS = ['Contatto', 'Deal', 'Conferma']
const STATUS_LABELS = { '0': 'Aperto', '1': 'Vinto', '2': 'Perso' }
const EMPTY_FORM = { email: '', name: '', surname: '', phone: '', deal_id: '' }
const Check = () => <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m5 12 4 4L19 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>

export default function ManualApplicationButton({ onCreated }) {
    const [open, setOpen] = useState(false)
    const [step, setStep] = useState(0)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')
    const [success, setSuccess] = useState('')
    const [lookup, setLookup] = useState(null)
    const [form, setForm] = useState(EMPTY_FORM)
    const [newDeal, setNewDeal] = useState(null)
    const [dealOptions, setDealOptions] = useState({ applications: [], owners: [] })
    const [stages, setStages] = useState([])
    const [loadingStages, setLoadingStages] = useState(false)
    const [remoteId, setRemoteId] = useState(null)
    const headingRef = useRef(null)
    const deals = lookup?.deals || []
    const selectedDeal = newDeal ? {
        id: remoteId, title: newDeal.title,
        application: dealOptions.applications.find(item => String(item.id) === newDeal.application_id),
    } : deals.find(deal => String(deal.id) === form.deal_id)
    const canContinue = selectedDeal && !selectedDeal.unavailable_reason

    useEffect(() => {
        if (!newDeal?.application_id) return
        let cancelled = false
        setLoadingStages(true)
        setStages([])
        api.get('/admin/users_applications/new_deal_options', { params: { application_id: newDeal.application_id } })
            .then(result => { if (!cancelled) setStages(result.stages || []) })
            .catch(e => { if (!cancelled) setError(errorText(e)) })
            .finally(() => { if (!cancelled) setLoadingStages(false) })
        return () => { cancelled = true }
    }, [newDeal?.application_id])

    const startNewDeal = async () => {
        setBusy(true)
        setError('')
        try {
            const options = await api.get('/admin/users_applications/new_deal_options')
            setDealOptions(options)
            setNewDeal({ application_id: '', title: '', owner_id: '', stage_id: '', status: '0', value: '0', currency: 'EUR', utm_source: '', utm_medium: '', utm_campaign: '', utm_content: '' })
        } catch (e) { setError(errorText(e)) }
        finally { setBusy(false) }
    }

    useEffect(() => { if (open) headingRef.current?.focus() }, [open, step])

    const start = () => {
        setOpen(true)
        setStep(0)
        setError('')
        setSuccess('')
        setLookup(null)
        setForm(EMPTY_FORM)
        setNewDeal(null)
        setRemoteId(null)
        setStages([])
    }

    const findUser = async event => {
        event.preventDefault()
        if (busy) return
        setBusy(true)
        setError('')
        setLookup(null)
        setNewDeal(null)
        setRemoteId(null)
        setForm(previous => ({ ...previous, deal_id: '' }))
        try {
            const result = await api.get('/admin/users_applications/user_lookup', { params: { email: form.email.trim() } })
            setLookup(result)
            setStep(1)
        } catch (e) { setError(errorText(e)) }
        finally { setBusy(false) }
    }

    const submit = async event => {
        event.preventDefault()
        if (busy || !canContinue) return
        setBusy(true)
        setError('')
        try {
            let dealId = remoteId || Number(form.deal_id)
            if (newDeal && !remoteId) {
                const remote = await api.post('/admin/users_applications/new_deal', {
                    email: form.email.trim(), name: form.name, surname: form.surname, phone: form.phone,
                    ...newDeal, application_id: Number(newDeal.application_id), owner_id: Number(newDeal.owner_id),
                    stage_id: Number(newDeal.stage_id), status: Number(newDeal.status),
                })
                dealId = remote.deal_id
                setRemoteId(dealId)
            }
            const result = await api.post('/admin/users_applications', {
                ...form, email: form.email.trim(), deal_id: dealId,
            })
            setOpen(false)
            setSuccess(`Candidatura #${result.id} creata e associata al deal #${result.deal_id}.${result.user_created ? ' Nuovo utente creato.' : ''} Se non compare nell’elenco, verifica i filtri attivi.`)
            onCreated?.()
        } catch (e) { setError(errorText(e)) }
        finally { setBusy(false) }
    }

    const back = () => { setError(''); setStep(previous => previous - 1) }
    const field = (key, label, options = {}) => <label className={styles.field}>
        <span>{label}</span>
        <input {...options} value={form[key]} disabled={busy} onChange={event => {
            setForm({ ...form, [key]: event.target.value, ...(key === 'email' ? { deal_id: '' } : {}) })
            setError('')
            if (key === 'email') setLookup(null)
        }} />
    </label>

    const dealField = (key, label, choices, inputProps = {}) => <label className={styles.field}>
        <span>{label}</span>
        {choices ? <select required value={newDeal[key]} disabled={busy || (key === 'stage_id' && loadingStages)} onChange={event => setNewDeal({ ...newDeal, [key]: event.target.value, ...(key === 'application_id' ? { stage_id: '' } : {}) })}>
            <option value="">{key === 'stage_id' && loadingStages ? 'Caricamento…' : 'Seleziona'}</option>
            {choices.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select> : <input required {...inputProps} value={newDeal[key]} onChange={event => setNewDeal({ ...newDeal, [key]: event.target.value })} />}
    </label>

    return <div>
        <CreateButton onClick={start}>Nuova opportunità</CreateButton>
        {success && <p role="status" className={styles.success}>{success}</p>}
        <Dialog open={open} title="Nuova candidatura" onClose={() => { if (!busy) setOpen(false) }}
            style={{ width: 'min(680px, 94vw)', maxHeight: '90dvh', borderRadius: '16px', boxShadow: '0 24px 80px #16213b33' }} contentMaxHeight="72dvh">
            <div className={styles.content} aria-busy={busy}>
                <ol className={styles.steps} aria-label="Avanzamento candidatura">
                    {STEP_LABELS.map((label, index) => <li key={label} aria-current={index === step ? 'step' : undefined} className={`${styles.step} ${index <= step ? styles.active : ''}`}>
                        <span className={styles.stepNumber}>{index < step ? <Check /> : index + 1}</span><span>{label}</span>
                    </li>)}
                </ol>
                <div className={styles.intro}>
                    <span className={styles.eyebrow}>PASSAGGIO {step + 1} DI 3</span>
                    <h2 tabIndex={-1} ref={headingRef}>{['Partiamo dall’email', 'Scegli il deal da collegare', 'Tutto pronto per la candidatura'][step]}</h2>
                    <p>{['Trova il contatto e i suoi deal su ActiveCampaign.', newDeal ? 'Scegli la candidatura e completa i dati del nuovo deal.' : 'La candidatura viene individuata automaticamente dalla pipeline del deal.', 'Controlla il riepilogo e completa i dati necessari.'][step]}</p>
                </div>

                {step === 0 && <form onSubmit={findUser} className={styles.form}>
                    {field('email', 'Email utente', { type: 'email', required: true, maxLength: 256, placeholder: 'nome@esempio.it', autoComplete: 'email' })}
                    <div className={styles.note}><span className={styles.noteIcon}>@</span><p>Hai già un utente? Lo ritroviamo dalla sua email. Se è nuovo, potrai aggiungere i suoi dati prima di confermare.</p></div>
                    {error && <p role="alert" className={styles.error}>{error}</p>}
                    <div className={styles.footer}><span className={styles.footerHint}>Ricerca su ActiveCampaign</span><Button type="submit" disabled={busy}>{busy ? 'Ricerca in corso…' : 'Cerca utente e deal'}</Button></div>
                </form>}

                {step === 1 && !newDeal && <div className={styles.form}>
                    <div className={styles.contact}><span className={styles.avatar}>{(lookup.user?.name || form.email).slice(0, 1).toUpperCase()}</span><div><strong>{lookup.user ? [lookup.user.name, lookup.user.surname].filter(Boolean).join(' ') || 'Utente esistente' : 'Nuovo utente'}</strong><span>{form.email.trim()}</span></div><span className={styles.badge}>{lookup.user ? 'Già presente' : 'Da creare'}</span></div>
                    <fieldset className={styles.deals}>
                        <legend>Deal disponibili <span>{deals.length}</span></legend>
                        {deals.map(deal => <label key={deal.id} className={`${styles.deal} ${String(deal.id) === form.deal_id ? styles.selected : ''} ${deal.unavailable_reason ? styles.unavailable : ''}`}>
                            <input type="radio" name="active-deal" value={deal.id} checked={String(deal.id) === form.deal_id} disabled={Boolean(deal.unavailable_reason)} onChange={() => setForm({ ...form, deal_id: String(deal.id) })} />
                            <div className={styles.dealBody}><div className={styles.dealTitle}><strong>{deal.title}</strong><span className={styles.badge}>{STATUS_LABELS[deal.status] || 'Deal'}</span></div>
                                <span className={styles.dealMeta}>#{deal.id} · Pipeline {deal.pipeline_id}</span>
                                {deal.application && <span className={styles.application}>{deal.application.name}</span>}
                                {deal.unavailable_reason && <span className={styles.reason}>{deal.unavailable_reason}</span>}
                            </div>
                        </label>)}
                    </fieldset>
                    {!deals.length && <div className={styles.empty}><strong>Nessun deal trovato</strong><p>{lookup.contact_found ? 'Il contatto esiste su ActiveCampaign, ma non ha deal disponibili.' : 'Nessun contatto trovato su ActiveCampaign con questa email.'}</p><span>Controlla l’indirizzo email e riprova.</span></div>}
                    {deals.length > 0 && deals.every(deal => deal.unavailable_reason) && <p className={styles.reason}>Nessun deal associabile. Le motivazioni sono indicate nelle schede.</p>}
                    {!deals.length && <Button disabled={busy} onClick={startNewDeal}>{busy ? 'Caricamento…' : 'Crea un nuovo deal'}</Button>}
                    {error && <p role="alert" className={styles.error}>{error}</p>}
                    <div className={styles.footer}><Button appearance="subtle" disabled={busy} onClick={back}>Indietro</Button><Button disabled={!canContinue} onClick={() => setStep(2)}>Continua</Button></div>
                </div>}

                {step === 1 && newDeal && <form className={styles.form} onSubmit={event => { event.preventDefault(); if (!loadingStages && selectedDeal.application) { setError(''); setStep(2) } }}>
                    {dealField('application_id', 'Candidatura e pipeline', dealOptions.applications.map(item => ({ id: item.id, name: `${item.name} · Pipeline ${item.pipeline_id}` })))}
                    {dealField('title', 'Titolo del deal', null, { maxLength: 255, pattern: '.*\\S.*', placeholder: 'Es. Candidatura al master' })}
                    <div className={styles.fieldGrid}>
                        {dealField('owner_id', 'Assegnatario ActiveCampaign', dealOptions.owners)}
                        {dealField('stage_id', 'Stage', stages.map(item => ({ id: item.id, name: item.title })))}
                        {dealField('value', 'Valore del deal', null, { type: 'number', min: 0, max: 999999999, step: '0.01' })}
                        {dealField('currency', 'Valuta', ['EUR', 'USD', 'GBP', 'CHF'].map(id => ({ id, name: id })))}
                    </div>
                    {dealField('status', 'Stato del deal', Object.entries(STATUS_LABELS).map(([id, name]) => ({ id, name })))}
                    <div className={styles.newUser}>
                        <div className={styles.sectionTitle}><h3>Provenienza della candidatura</h3><span className={styles.badge}>Facoltativo</span></div>
                        <p>Gli UTM verranno salvati sul deal e riportati nella candidatura.</p>
                        <div className={styles.fieldGrid}>
                            {dealField('utm_source', 'UTM source', null, { required: false, maxLength: 1024, placeholder: 'Es. google' })}
                            {dealField('utm_medium', 'UTM medium', null, { required: false, maxLength: 1024, placeholder: 'Es. cpc' })}
                            {dealField('utm_campaign', 'UTM campaign', null, { required: false, maxLength: 1024, placeholder: 'Es. master_finance' })}
                            {dealField('utm_content', 'UTM content', null, { required: false, maxLength: 1024, placeholder: 'Es. annuncio_video' })}
                        </div>
                    </div>
                    <p className={styles.caption}>Il valore indicato sarà salvato sul deal senza conversioni IVA. Il nuovo deal avrà origine sf_backend.</p>
                    {!dealOptions.applications.length && <p className={styles.reason}>Nessuna candidatura con pipeline univoca disponibile.</p>}
                    {newDeal.application_id && !loadingStages && !stages.length && <p className={styles.reason}>Nessuno stage disponibile. Seleziona nuovamente la candidatura per riprovare.</p>}
                    {error && <p role="alert" className={styles.error}>{error}</p>}
                    <div className={styles.footer}><Button type="button" appearance="subtle" onClick={() => { setNewDeal(null); setError('') }}>Indietro</Button><Button type="submit" disabled={loadingStages || !selectedDeal.application || !newDeal.stage_id}>Continua</Button></div>
                </form>}

                {step === 2 && <form onSubmit={submit} className={styles.form}>
                    <div className={styles.summary}><span className={styles.eyebrow}>CANDIDATURA SELEZIONATA</span><h3>{selectedDeal.application.name}</h3><div className={styles.summaryRow}><span>Deal ActiveCampaign</span><strong>{selectedDeal.id ? `#${selectedDeal.id}` : 'Da creare'} · {selectedDeal.title}</strong></div><div className={styles.summaryRow}><span>Contatto</span><strong>{form.email.trim()}</strong></div></div>
                    {newDeal && <div className={styles.summary}>
                        <div className={styles.summaryRow}><span>Stage</span><strong>{stages.find(item => String(item.id) === newDeal.stage_id)?.title}</strong></div>
                        <div className={styles.summaryRow}><span>Assegnatario deal</span><strong>{dealOptions.owners.find(item => String(item.id) === newDeal.owner_id)?.name}</strong></div>
                        <div className={styles.summaryRow}><span>Valore e stato</span><strong>{newDeal.value} {newDeal.currency} · {STATUS_LABELS[newDeal.status]}</strong></div>
                        {['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'].filter(key => newDeal[key]?.trim()).map(key => <div key={key} className={styles.summaryRow}><span>{key.replace('utm_', 'UTM ')}</span><strong>{newDeal[key].trim()}</strong></div>)}
                    </div>}
                    {remoteId && <p role="status" className={styles.caption}>Deal #{remoteId} creato su ActiveCampaign. Riprova il salvataggio per completare la candidatura senza creare un altro deal.</p>}
                    {lookup.user ? <div className={styles.note}><span className={styles.noteIcon}><Check /></span><p><strong>Utente già presente</strong><br />{[lookup.user.name, lookup.user.surname].filter(Boolean).join(' ') || lookup.user.email} · #{lookup.user.id}</p></div> : <div className={styles.newUser}><div className={styles.sectionTitle}><h3>Completa il nuovo utente</h3><span className={styles.badge}>Nuovo</span></div><p>Nome e cognome sono obbligatori. L’utente verrà creato insieme alla candidatura.</p><div className={styles.fieldGrid}>{field('name', 'Nome', { required: true, pattern: '.*\\S.*', maxLength: 256, autoComplete: 'given-name', placeholder: 'Nome' })}{field('surname', 'Cognome', { required: true, pattern: '.*\\S.*', maxLength: 256, autoComplete: 'family-name', placeholder: 'Cognome' })}</div>{field('phone', 'Telefono (facoltativo)', { type: 'tel', maxLength: 256, autoComplete: 'tel', placeholder: '+39' })}</div>}
                    <p className={styles.caption}>Lo stato iniziale viene ricavato dal deal. La candidatura sarà assegnata al referente configurato.</p>
                    {error && <p role="alert" className={styles.error}>{error}</p>}
                    <div className={styles.footer}><Button type="button" appearance="subtle" disabled={busy || Boolean(remoteId)} onClick={back}>Indietro</Button><Button type="submit" disabled={busy || !canContinue}>{busy ? 'Creazione in corso…' : 'Crea candidatura'}</Button></div>
                </form>}
            </div>
        </Dialog>
    </div>
}
